import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { connect } from 'react-redux';

import { Typography } from '@comparethemarketau/manor-typography';
import { Link } from '@comparethemarketau/manor-link';
import { Button } from '@comparethemarketau/manor-button';
import { DrawerHorizontal } from '@comparethemarketau/manor-drawer';

import { ClickableChip } from './clickable_chip';
import { excessStateMapping, excessDispatchMapping } from './filter_store_mapping';
import { findGlobalFontSize } from '../../sorts/common/find_global_font_size';

const useStyles = createUseStyles((theme) => ({
    buttonIcon: {
        marginLeft: theme.spacing[4],
        fontSize: theme.fontSize.sm
    },
    buttonDisabled: {
        color: theme.colors.grey700,
        cursor: 'not-allowed'
    },
    drawerContainer: {
        '& .StyledDrawerActive': {
            borderTop: `${theme.spacing[4]} solid ${theme.colors.primary500}`
        }
    },
    contentContainer: {
        width: '26rem',
        margin: 'auto'
    },
    excessAmountTitle: {
        color: theme.colors.grey700,
        fontWeight: theme.fontWeight.bold,
        fontSize: theme.fontSize.base,
        marginBottom: theme.spacing[8]
    },
    excessAmountSubTitle: {
        color: theme.colors.grey700,
        fontWeight: theme.fontWeight.normal,
        fontSize: theme.fontSize.xs,
        marginBottom: theme.spacing[12]
    },
    excessAmountGridContainer: {
        marginBottom: theme.spacing[12]
    },
    excessAmountRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: theme.spacing[12]
    },
    updateExcessButtonContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingTop: theme.spacing[12],
        paddingBottom: theme.spacing[12]
    },
    updateExcessButton: {
        margin: `0 0 0 ${theme.spacing[28]}`,
        width: '13.8rem',
        height: '4.8rem'
    },
    cancelButton: {
        margin: 0
    }

}));

const _ExcessFilterDrawer = (props) => {
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme });

    const { selectedValue, stageChange, disabled, stagedValue, undoStaged, applyUpdateResults } = props;

    const [visible, setVisible] = useState(false);
    const [htmlFontSize, setHtmlFontSize] = useState(10);

    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);

    useEffect(() => {
        setHtmlFontSize(findGlobalFontSize());
    }, []);

    useEffect(() => {
        if (visible && stagedValue !== selectedValue) {
            setUpdateButtonDisabled(false);
        } else {
            setUpdateButtonDisabled(true);
        }
    }, [stagedValue, visible]);

    const handleClick = (e) => {
        e.preventDefault();
        if (!disabled) {
            setVisible(true);
        }
    };

    const handleClose = () => {
        setVisible(false);
        if (stagedValue !== selectedValue) {
            undoStaged();
        }
    };

    const handleChipClick = (value) => {
        stageChange(value);
    };

    const handleUpdateExcess = () => {
        applyUpdateResults(true);
    };

    return (
        <div className={classNames('test-policyFilters-excess')}>
            <Link
                handleClick={handleClick}
                underline="none"
                data-testid="excess-drawer"
                trackingLabel={'Button for open excess filter drawer'}
            >
                <div className={disabled && classes.buttonDisabled}>
                    <Typography display="inline" variant="subtitle2">
                        {`$${stagedValue} Excess`}
                    </Typography>
                    <FontAwesomeIcon icon={faChevronDown} className={classes.buttonIcon} />
                </div>
            </Link>
            <div className={classes.drawerContainer}>
                <DrawerHorizontal
                    trackingLabel="excess filter drawer"
                    visible={visible}
                    handleOverlayClick={handleClose}
                    handleClose={handleClose}
                    size={37.4 * htmlFontSize}
                    overlay
                    keyLine
                    overlayOpacity={0.5}
                >
                    <div className={classes.contentContainer}>
                        <Typography className={classes.excessAmountTitle}>Excess amount</Typography>
                        <Typography className={classes.excessAmountSubTitle}>You can trailor your premium by varying your excess</Typography>
                        <div className={classes.excessAmountGridContainer}>
                            <div className={classes.excessAmountRow}>
                                <ClickableChip label="$500" value="500" selected={stagedValue === '500'} onClick={handleChipClick} />
                                <ClickableChip label="$600" value="600" selected={stagedValue === '600'} onClick={handleChipClick} />
                                <ClickableChip label="$700" value="700" selected={stagedValue === '700'} onClick={handleChipClick} />
                                <ClickableChip label="$800" value="800" selected={stagedValue === '800'} onClick={handleChipClick} />
                            </div>
                            <div className={classes.excessAmountRow}>
                                <ClickableChip label="$900" value="900" selected={stagedValue === '900'} onClick={handleChipClick} />
                                <ClickableChip label="$1000" value="1000" selected={stagedValue === '1000'} onClick={handleChipClick} />
                                <ClickableChip label="$1100" value="1100" selected={stagedValue === '1100'} onClick={handleChipClick} />
                                <ClickableChip label="$1200" value="1200" selected={stagedValue === '1200'} onClick={handleChipClick} />
                            </div>
                            <div className={classes.excessAmountRow}>
                                <ClickableChip label="$1300" value="1300" selected={stagedValue === '1300'} onClick={handleChipClick} />
                                <ClickableChip label="$1400" value="1400" selected={stagedValue === '1400'} onClick={handleChipClick} />
                                <ClickableChip label="$1500" value="1500" selected={stagedValue === '1500'} onClick={handleChipClick} />
                                <ClickableChip label="$1600" value="1600" selected={stagedValue === '1600'} onClick={handleChipClick} />
                            </div>
                            <div className={classes.excessAmountRow}>
                                <ClickableChip label="$1700" value="1700" selected={stagedValue === '1700'} onClick={handleChipClick} />
                                <ClickableChip label="$1800" value="1800" selected={stagedValue === '1800'} onClick={handleChipClick} />
                                <ClickableChip label="$1900" value="1900" selected={stagedValue === '1900'} onClick={handleChipClick} />
                                <ClickableChip label="$2000" value="2000" selected={stagedValue === '2000'} onClick={handleChipClick} />
                            </div>
                        </div>
                        <div className={classes.updateExcessButtonContainer}>
                            <div>
                                <Button data-testid="cancel-excess-button" className={classes.cancelButton} trackingLabel="Button for close excess drawer" variant="tertiary" handleClick={handleClose}>Cancel</Button>
                            </div>
                            <div>
                                <Button
                                    data-testid="update-excess-button"
                                    className={classes.updateExcessButton}
                                    trackingLabel="Button for update excess"
                                    variant="primary"
                                    disabled={updateButtonDisabled}
                                    handleClick={handleUpdateExcess}
                                >
                                    Update excess
                                </Button>
                            </div>
                        </div>
                    </div>
                </DrawerHorizontal>
            </div>
        </div>
    );
};

_ExcessFilterDrawer.propTypes = {
    disabled: PropTypes.bool,
    selectedValue: PropTypes.string,
    stagedValue: PropTypes.string,
    stageChange: PropTypes.func,
    undoStaged: PropTypes.func,
    applyUpdateResults: PropTypes.func
};

const ExcessFilterDrawer = connect(excessStateMapping, excessDispatchMapping)(_ExcessFilterDrawer);

export { ExcessFilterDrawer };
